/*
Account entry: the header icon asks the account (account.thehype.news, origin from
data-account-origin) whether the visitor is signed in. Signed in — the icon shows an
avatar placeholder (partials/icons/avatar.hbs) and leads to the account. Otherwise a plain click opens
the sign-in dialog. Google and GitHub post to the account as top-level navigation; the
email form opens the account sign-in page with the address filled in, where the
captcha runs (an hCaptcha challenge cannot work above a modal dialog). return_to is
this page, so the visitor comes back here after signing in.

/me also says which sign-in methods work. Only the answer to the latest request is
applied: an older answer arriving late must not undo a newer one. "last used" marks
the method the visitor picked last time (localStorage, a hint only). If /me does not
answer, or the browser has no <dialog>, the icon stays a plain link to the account.
*/

const LAST_USED_KEY = 'th-last-signin';

export default function account() {
    const link = document.querySelector('[data-account]');
    const dialog = document.querySelector('[data-signin]');
    if (!link || !dialog || !window.fetch || typeof dialog.showModal !== 'function') {
        return;
    }
    const origin = link.dataset.accountOrigin;
    let state = null;
    let latest = 0;

    async function refresh() {
        latest += 1;
        const request = latest;
        try {
            const reply = await fetch(`${origin}/me`, { credentials: 'include' });
            const answer = reply.ok ? await reply.json() : null;
            if (answer && request === latest) {
                state = answer;
                render();
            }
        } catch (error) {
            // The account is unreachable: the icon keeps working as a plain link.
        }
    }

    function render() {
        const avatar = link.querySelector('.th-account-avatar');
        const icon = link.querySelector('.th-account-icon');
        const signedIn = state.signed_in === true;
        link.dataset.state = signedIn ? 'signed-in' : 'signed-out';
        link.href = signedIn ? `${origin}/` : `${origin}/login`;
        link.setAttribute('aria-label', signedIn ? 'Your thehype account' : 'Sign in');
        avatar.hidden = !signedIn;
        icon.hidden = signedIn;
        if (signedIn) {
            // Signed in from another tab: the open dialog has nothing left to offer.
            if (dialog.open) {
                dialog.close();
            }
            return;
        }
        const providers = state.providers || [];
        dialog.querySelectorAll('[data-signin-form]').forEach((form) => {
            const method = form.dataset.signinForm;
            form.hidden = method === 'email' ? state.email !== true : !providers.includes(method);
        });
        dialog.querySelector('.th-signin-or').hidden = !(state.email === true && providers.length > 0);
    }

    function open() {
        const back = location.href.split('#')[0];
        dialog.querySelectorAll('[data-signin-form]').forEach((form) => {
            form.action = `${origin}${form.dataset.path}`;
            form.querySelector('[name="return_to"]').value = back;
        });
        markLastUsed();
        dialog.showModal();
    }

    function markLastUsed() {
        let last = null;
        try {
            last = localStorage.getItem(LAST_USED_KEY);
        } catch (error) {
            last = null;
        }
        dialog.querySelectorAll('[data-signin-form]').forEach((form) => {
            form.querySelector('.th-signin-last').hidden = form.dataset.signinForm !== last;
        });
    }

    function isPlainClick(event) {
        return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    link.addEventListener('click', (event) => {
        if (!state || state.signed_in || !isPlainClick(event)) {
            return;
        }
        event.preventDefault();
        open();
    });

    dialog.addEventListener('submit', (event) => {
        try {
            localStorage.setItem(LAST_USED_KEY, event.target.dataset.signinForm);
        } catch (error) {
            // Private mode: "last used" is only a hint.
        }
    });

    dialog.addEventListener('click', (event) => {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === dialog || event.target.closest('[data-signin-close]')) {
            dialog.close();
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            refresh();
        }
    });

    // A page restored from the back/forward cache keeps the old sign-in state.
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            refresh();
        }
    });

    refresh();
}
